import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;
  const current = open ? images[index] : null;

  const prev = useCallback(() => onNavigate((index - 1 + images.length) % images.length), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % images.length), [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[90] bg-night/95 backdrop-blur-md flex flex-col"
          onClick={onClose}
          data-testid="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Photo gallery"
        >
          <div className="flex items-center justify-between px-5 sm:px-8 py-4 text-sand/70">
            <span className="font-mono text-xs tracking-[0.2em] uppercase" data-testid="lightbox-counter">
              {index + 1} / {images.length}
            </span>
            <button
              onClick={onClose}
              data-testid="lightbox-close-btn"
              aria-label="Close gallery"
              className="p-2.5 rounded-full border border-sand/20 hover:bg-sand hover:text-night transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="relative grow flex items-center justify-center px-4 sm:px-20 pb-4 min-h-0">
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev-btn"
              aria-label="Previous photo"
              className="absolute left-3 sm:left-6 z-10 p-3 rounded-full border border-sand/20 text-sand hover:bg-sand hover:text-night transition-colors"
            >
              <ChevronLeft size={20} />
            </button>

            <AnimatePresence mode="wait">
              <motion.figure
                key={index}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="max-w-5xl w-full flex flex-col items-center min-h-0"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={current.src}
                  alt={current.caption}
                  data-testid="lightbox-image"
                  className="max-h-[72vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
                />
                <figcaption className="mt-4 text-sand/80 font-serif text-lg text-center" data-testid="lightbox-caption">
                  {current.caption}
                </figcaption>
              </motion.figure>
            </AnimatePresence>

            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next-btn"
              aria-label="Next photo"
              className="absolute right-3 sm:right-6 z-10 p-3 rounded-full border border-sand/20 text-sand hover:bg-sand hover:text-night transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
